// Story 7.1 (ADO-904) — one browse column's children, shared by the mega-menu
// columns (BrowseNav) and the mobile side tray (BrowseTray, ADO-1779).

import { useEffect, useState } from 'react';
import { type CategoryNode, fetchChildren } from '../../api/browse.client';
import { useCatalogueEntryKey } from '../../ordering/catalogueEntry';

export interface ColumnState {
  readonly label: string;
  readonly nodes: readonly CategoryNode[];
  readonly loading: boolean;
  readonly error: string | null;
}

const EMPTY_COLUMN: ColumnState = { label: '', nodes: [], loading: false, error: null };

/**
 * Load a node's children (or top-level departments when parentId is null) into
 * column state. `enabled` gates the fetch: a not-yet-drilled child column would
 * otherwise call `fetchChildren(null)` on mount and waste a round-trip fetching
 * (then discarding) the Departments — so those columns stay idle until active.
 */
export function useCategoryChildren(
  parentId: string | null,
  label: string,
  enabled: boolean,
): ColumnState {
  const [state, setState] = useState<ColumnState>({ ...EMPTY_COLUMN, label, loading: enabled });
  // Enabler E7-C (ADO-2274), D-1: the tree is the selected entry's range; with no entry the column
  // stays idle, and an entry change reloads it.
  const catalogueEntryKey = useCatalogueEntryKey();

  useEffect(() => {
    if (!enabled || catalogueEntryKey === null) {
      setState({ label, nodes: [], loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ label, nodes: [], loading: true, error: null });
    fetchChildren(catalogueEntryKey, parentId)
      .then((res) => {
        if (!cancelled) setState({ label, nodes: res.children, loading: false, error: null });
      })
      .catch(() => {
        if (!cancelled)
          setState({ label, nodes: [], loading: false, error: 'Could not load this list.' });
      });
    return () => {
      cancelled = true;
    };
  }, [parentId, label, enabled, catalogueEntryKey]);

  return state;
}
